/**
 * TrajectoryRing.jsx — Anneau central du Score de Trajectoire.
 *
 * Grande jauge SVG (0-100) + 12 chips de dimensions cliquables.
 * Utilisée dans le hero du Morning Command Center pour donner
 * une lecture immédiate du système nerveux d'ALTIORA.
 */
import { motion } from "framer-motion";
import { useState } from "react";
import {
  Target, DollarSign, Users, Megaphone, Handshake, CalendarCheck,
  Brain, GraduationCap, Wallet, HeartPulse, ShieldCheck, Compass,
  TrendingUp, TrendingDown, Minus, ChevronDown, ChevronUp,
} from "lucide-react";
import {
  Card, Pill, NAVY, CHAMPAGNE, GOLD, IVORY, SLATE, GREEN_LEAF, RED_LEAF,
} from "@/components/altiora/PremiumUI";

const DIM_ICON = {
  commercial: Target, revenue: DollarSign, clients: Users,
  marketing: Megaphone, network: Handshake, organization: CalendarCheck,
  skills: Brain, training: GraduationCap, finance: Wallet,
  wellbeing: HeartPulse, compliance: ShieldCheck, career: Compass,
};

function bandColor(band) {
  if (band === "strong") return GREEN_LEAF;
  if (band === "moderate") return CHAMPAGNE;
  return RED_LEAF;
}

function bandLabel(band) {
  if (band === "strong") return "Solide";
  if (band === "moderate") return "À consolider";
  return "Fragile";
}


// ═══════════════════════════════════════════════════════════════════
// The main ring
// ═══════════════════════════════════════════════════════════════════
export default function TrajectoryRing({ trajectory, onDimensionClick }) {
  const [expanded, setExpanded] = useState(false);
  if (!trajectory) return null;
  const { global_score, band, dimensions, headline, delta_7d, trust_index } = trajectory;

  const radius = 80;
  const stroke = 12;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (global_score / 100) * circumference;
  const color = bandColor(band);

  const deltaIcon = delta_7d > 0 ? TrendingUp : delta_7d < 0 ? TrendingDown : Minus;
  const DeltaIcon = deltaIcon;
  const deltaColor = delta_7d > 0 ? "#065F46" : delta_7d < 0 ? "#B91C1C" : SLATE;

  return (
    <div data-testid="trajectory-ring-root" className="rounded-2xl p-6"
         style={{
           background: `linear-gradient(135deg, ${IVORY} 0%, #FFFFFF 100%)`,
           border: `1px solid ${CHAMPAGNE}33`,
           boxShadow: "0 4px 24px -8px rgba(11,37,69,0.10)",
         }}>
      <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
        {/* Ring */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.2, 0.7, 0.2, 1] }}
          className="relative flex-shrink-0"
          data-testid="trajectory-ring-svg-wrap"
        >
          <svg width="200" height="200" viewBox="0 0 200 200">
            <circle cx="100" cy="100" r={radius} fill="none"
                    stroke={`${SLATE}33`} strokeWidth={stroke} />
            <motion.circle
              cx="100" cy="100" r={radius} fill="none"
              stroke={color} strokeWidth={stroke} strokeLinecap="round"
              strokeDasharray={circumference}
              initial={{ strokeDashoffset: circumference }}
              animate={{ strokeDashoffset: offset }}
              transition={{ duration: 1.2, ease: [0.2, 0.7, 0.2, 1] }}
              transform="rotate(-90 100 100)"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-[10px] font-mono uppercase tracking-[0.28em]" style={{ color: SLATE }}>
              Score de trajectoire
            </div>
            <div className="font-serif tabular-nums leading-none mt-1"
                 style={{ color: NAVY, fontSize: 56 }}
                 data-testid="trajectory-global-score">
              {global_score}
            </div>
            <div className="text-[11px] opacity-70" style={{ color: NAVY }}>/ 100</div>
          </div>
        </motion.div>

        {/* Headline + delta + trust */}
        <div className="flex-1 min-w-0 text-center md:text-left">
          <Pill tone={band === "strong" ? "green" : band === "moderate" ? "amber" : "red"} testid="trajectory-band-pill">
            {bandLabel(band)}
          </Pill>
          <h2 className="mt-3 font-serif text-xl md:text-2xl tracking-tight" style={{ color: NAVY }}
              data-testid="trajectory-headline">
            {headline}
          </h2>
          <div className="mt-3 flex items-center gap-4 flex-wrap justify-center md:justify-start text-[13px]">
            <span data-testid="trajectory-delta-7d" className="inline-flex items-center gap-1.5"
                  style={{ color: deltaColor }}>
              <DeltaIcon className="h-4 w-4" strokeWidth={2} />
              {delta_7d > 0 ? `+${delta_7d}` : delta_7d} pts sur 7 j
            </span>
            <span data-testid="trajectory-trust" className="inline-flex items-center gap-1.5"
                  style={{ color: SLATE }}>
              <span className="text-[10px] font-mono uppercase tracking-[0.22em]">Confiance du jumeau</span>
              <strong style={{ color: NAVY }}>{trust_index}/100</strong>
            </span>
          </div>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.28em] hover:opacity-70 transition-opacity"
            style={{ color: CHAMPAGNE }}
            data-testid="trajectory-toggle"
          >
            {expanded ? "Masquer" : "Voir"} les 12 dimensions
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
        </div>
      </div>

      {/* 12 dimensions */}
      {expanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          transition={{ duration: 0.35 }}
          className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
          data-testid="trajectory-dimensions-grid"
        >
          {dimensions.map((d) => (
            <DimensionChip
              key={d.key}
              dim={d}
              onClick={() => onDimensionClick?.(d)}
            />
          ))}
        </motion.div>
      )}
    </div>
  );
}


function DimensionChip({ dim, onClick }) {
  const Icon = DIM_ICON[dim.key] || Target;
  const color = bandColor(dim.band);
  const deltaColor = dim.delta_7d > 0 ? "#065F46" : dim.delta_7d < 0 ? "#B91C1C" : SLATE;
  return (
    <button
      onClick={onClick}
      className="text-left rounded-xl p-3 transition-all hover:-translate-y-0.5"
      style={{
        background: "#FFFFFF",
        border: `1px solid ${CHAMPAGNE}22`,
        boxShadow: "0 1px 4px -2px rgba(11,37,69,0.06)",
      }}
      data-testid={`dim-chip-${dim.key}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0"
               style={{ background: `${color}22`, color }}>
            <Icon className="h-4 w-4" strokeWidth={1.8} />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] truncate"
                 style={{ color: NAVY }}>
              {dim.label}
            </div>
            <div className="text-[10px]" style={{ color: SLATE }}>
              {bandLabel(dim.band)}
            </div>
          </div>
        </div>
        <div className="font-serif text-lg tabular-nums" style={{ color: NAVY }}>
          {dim.score}
        </div>
      </div>
      {dim.delta_7d !== 0 && (
        <div className="mt-1.5 text-[10px]" style={{ color: deltaColor }}>
          {dim.delta_7d > 0 ? `+${dim.delta_7d}` : dim.delta_7d} pts / 7 j
        </div>
      )}
    </button>
  );
}
